import { useState } from "react";

import { useToast } from "../../../contexts/ToastContext";

export function Downloads() {
  const { toast } = useToast();
  const [readme, setReadme] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function loadReadme() {
    if (readme !== null) {
      setReadme(null);
      return;
    }
    setLoading(true);
    try {
      // The README is embedded in the backend (downloads/pi-setup-readme),
      // not a static file in the SPA bundle. The previous /README.md path
      // 404'd in production (or worse: nginx's SPA fallback returned the
      // index.html, which then rendered as garbled HTML).
      const res = await fetch("/api/downloads/pi-setup-readme");
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const text = await res.text();
      setReadme(text);
    } catch {
      toast("Could not load README — only available on a configured server.", "error");
    } finally {
      setLoading(false);
    }
  }

  // Fetch + blob-download instead of <a href download>. With <a href>, a
  // server error (404 missing image, 500 missing script) navigates the
  // browser to the broken URL and shows the OS "site unavailable" page,
  // pushing the user out of the SPA. Doing it in JS keeps failures local
  // and lets us surface them as a toast.
  async function downloadFile(url: string, filename: string, errorLabel: string) {
    try {
      const res = await fetch(url);
      if (!res.ok) {
        // Backend returns {"detail": "..."} for 404s — surface that to the
        // user (e.g. "No Pi image available. Upload a .zip ...").
        const body = (await res.json().catch(() => null)) as { detail?: string } | null;
        throw new Error(body?.detail ?? `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (e) {
      const msg = e instanceof Error ? e.message : errorLabel;
      toast(msg, "error");
    }
  }

  return (
    <>
      <h2 className="page-title">Downloads</h2>

      <div className="card">
        <div className="card-header">
          <h3>
            Option 1 — Pre-Built Pi Image{" "}
            <span
              style={{
                fontWeight: 400,
                color: "var(--green)",
                fontSize: 12,
                background: "rgba(46,204,113,.12)",
                padding: "2px 8px",
                borderRadius: 4,
                marginLeft: 8,
              }}
            >
              Recommended
            </span>
          </h3>
        </div>
        <p style={{ color: "var(--text2)", fontSize: 13, marginBottom: 16 }}>
          Flash this image directly to an SD card. The Pi boots straight into the SEILAN setup
          screen — no SSH or manual configuration needed.
        </p>
        <div className="download-card">
          <div
            className="dl-icon"
            style={{ background: "linear-gradient(135deg,#2ecc71 0%,#27ae60 100%)", color: "#fff", border: "none" }}
          >
            IMG
          </div>
          <div className="info">
            <h4>SEILAN Player Image</h4>
            <p>Ready-to-flash Raspberry Pi OS image with SEILAN pre-installed.</p>
          </div>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() =>
              downloadFile("/api/downloads/pi-image", "seilan-pi-image.zip", "Could not download Pi image.")
            }
          >
            Download .zip
          </button>
        </div>
        <div
          style={{
            marginTop: 12,
            padding: 16,
            background: "var(--surface2)",
            borderRadius: 8,
            fontSize: 13,
            color: "var(--text2)",
            lineHeight: 1.8,
          }}
        >
          <strong style={{ color: "var(--text)" }}>Setup Steps:</strong>
          <br />
          1. Download the image above and unzip it
          <br />
          2. Flash the <code>.img</code> file to an SD card using Balena Etcher
          <br />
          3. Insert the SD card into the Pi and power it on
          <br />
          4. On the setup screen, select your WiFi and enter the server URL
          <br />
          5. The Pi will register itself — claim it from the <strong>Pi Screens</strong> tab
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h3>Option 2 — Manual Setup Script</h3>
        </div>
        <p style={{ color: "var(--text2)", fontSize: 13, marginBottom: 16 }}>
          For advanced users. Requires a fresh Raspberry Pi OS Lite 64-bit install and SSH access.
        </p>
        <div className="download-card">
          <div className="dl-icon">SH</div>
          <div className="info">
            <h4>pi-setup.sh</h4>
            <p>Bash script to configure any Raspberry Pi as a SEILAN player.</p>
          </div>
          <button
            type="button"
            className="btn btn-outline"
            onClick={() =>
              downloadFile("/api/downloads/pi-setup", "pi-setup.sh", "Could not download pi-setup.sh.")
            }
          >
            Download
          </button>
        </div>
        <div className="download-card">
          <div className="dl-icon">?</div>
          <div className="info">
            <h4>Manual Instructions</h4>
            <p>Step-by-step guide for the script-based setup.</p>
          </div>
          <button type="button" className="btn btn-outline" onClick={loadReadme} disabled={loading}>
            {loading ? "Loading…" : readme !== null ? "Hide" : "View"}
          </button>
        </div>
        {readme !== null && (
          <pre
            style={{
              marginTop: 16,
              padding: 20,
              background: "var(--surface2)",
              borderRadius: 8,
              whiteSpace: "pre-wrap",
              fontSize: 13,
              fontFamily: "monospace",
              color: "var(--text2)",
            }}
          >
            {readme}
          </pre>
        )}
      </div>
    </>
  );
}
